import './subscribe.less'

import {Background} from '@codeurs/front'
import placeholder from 'assets/images/placeholder.jpg'
import {Form, Formik} from 'formik'
import requestService from 'mithril/request/request'
import React, {useState} from 'react'
import {ImageType, WysiwygType} from 'types'
import {t} from 'util/i18n'
import {style} from 'util/style'
import {FormField} from 'views/form/form'
import {PopupModal} from 'views/layout/popupmodal'
import {Theme} from 'views/theme'

const request = requestService(window, Promise).request

const styles = style('subscribe', ['form'])

export type SubscribeType = {
	image?: ImageType
	title?: string
	eloqua_post_url?: string
	text?: WysiwygType
	agreement?: WysiwygType
	thanks_title?: string
	thanks_text?: WysiwygType
	checkboxes: Array<{
		label: string
		eloqua_field: string
	}>
}

type FormData = {
	firstname?: string
	lastname?: string
	email?: string
	agree?: boolean
} & Record<string, string>

export const Subscribe: React.FC<SubscribeType> = data => {
	const {image, title, text} = data

	return (
		<div className={styles()} id="subscribe">
			<Theme.OuterContainer>
				<div className={styles('row')}>
					<div className={styles('image')}>
						<Background img={image ? image : placeholder} />
						<div className={styles('image-overlay')} />
						{/* <span className={styles('image-stuff')} /> */}
						{/* <span className={styles('image-dots')} /> */}
					</div>
					<div className={styles('content')}>
						<div className={styles('content-container')}>
							{title && (
								<Theme.H3 className={styles('content-title')}>{title}</Theme.H3>
							)}
							{text && (
								<div className={styles('content-text')}>
									<Theme.Wysiwyg mod="inherit">{text}</Theme.Wysiwyg>
								</div>
							)}
							<SubscribeForm {...data} />
						</div>
					</div>
				</div>
			</Theme.OuterContainer>
		</div>
	)
}

function sendDataToEloqua(config: SubscribeType, data: FormData) {
	if (!config.eloqua_post_url) return

	const formData = new URLSearchParams()
	formData.append('firstname', data.firstname)
	formData.append('lastname', data.lastname)
	formData.append('email', data.email)
	formData.append('agree_terms', 'True')

	config.checkboxes.forEach(checkbox => {
		if (data[checkbox.eloqua_field]) {
			formData.append(checkbox.eloqua_field, 'True')
		} else {
			formData.append(checkbox.eloqua_field, 'False')
		}
	})

	return fetch(config.eloqua_post_url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: formData
	})
}

export const SubscribeForm: React.FC<SubscribeType> = config => {
	const {checkboxes, agreement, thanks_title, thanks_text} = config
	const [isSubmitted, setSubmitted] = useState(false)

	return (
		<Formik<FormData>
			initialValues={{}}
			onSubmit={(values, {setErrors, resetForm}) =>
				request({
					method: 'POST',
					url: `/api/subscribe`,
					data: values
				})
					.then(response => {
						resetForm()
						setSubmitted(true)
						sendDataToEloqua(config, values)
					})
					.catch(error => {
						setErrors(JSON.parse(error.message))
					})
			}
		>
			<div className={styles.form()}>
				<Form>
					<FormField
						required
						name="firstname"
						label={t('subscribe.firstname')}
						mod="background"
					/>
					<FormField
						required
						name="lastname"
						label={t('subscribe.lastname')}
						mod="background"
					/>
					<FormField
						required
						type="email"
						name="email"
						label={t('subscribe.email')}
						mod="background"
					/>
					{(checkboxes || [])
						.filter(checkbox => checkbox.label)
						.map(checkbox => (
							<FormField
								type="checkbox"
								name={checkbox.eloqua_field}
								label={checkbox.label}
								mod="small"
							/>
						))}
					<FormField
						required
						type="checkbox"
						name="agree"
						label={
							<Theme.Wysiwyg mod="inherit">
								{agreement || t('subscribe.agreement')}
							</Theme.Wysiwyg>
						}
						mod="small"
					/>
					<div className={styles.form('button')}>
						<Theme.Button
							as="button"
							type="submit"
							mod={['arrowlong', 'white']}
						>
							<Theme.ButtonText>{t('subscribe.submit')}</Theme.ButtonText>
							<Theme.ButtonIcon icon="arrow-long" />
						</Theme.Button>
					</div>
				</Form>
				<PopupModal
					open={isSubmitted}
					onClose={() => {
						setSubmitted(false)
					}}
				>
					<PopupModal.Block>
						<PopupModal.Content mod="big">
							<Theme.H1 as="h2" mod={['dots']}>
								{thanks_title || t('subscribe.thanks.title')}
							</Theme.H1>
							<PopupModal.Message>
								<Theme.Wysiwyg>
									{thanks_text || t('subscribe.thanks.text')}
								</Theme.Wysiwyg>
							</PopupModal.Message>
						</PopupModal.Content>
						<PopupModal.Footer />
					</PopupModal.Block>
				</PopupModal>
			</div>
		</Formik>
	)
}
